/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	useContext,
	useEffect,
	useMemo,
	useState,
	useRef,
	forwardRef,
	Fragment,
} from 'react';
// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
import uuid from 'uuid/v4';

import hash from 'object-hash';

import { css, jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { ForgeContextToken } from '@atlassian/forge-ui-types';
import { useMergeRefs } from 'use-callback-ref';
import { fg } from '@atlaskit/platform-feature-flags';

import { EnvironmentContext } from '../../context';
import {
	type SubscribeRealtimeChannelPayload,
	type PublishRealtimeChannelPayload,
	useInvokeExtension,
	useSubscribeRealtimeChannel,
	usePublishRealtimeChannel,
	useEmitPublicEvent,
} from '../../web-client';
import { ThreeLOPrompt } from '../../components';
import {
	useBridge,
	useProductFetchClient,
	useNavigation,
	createBridgeApi,
	isBinaryContentType,
	useRovoAgentsChat,
	usePermission,
} from '../bridge';
import { useTracingContext } from '../../error-reporting';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import {
	getBrowserLocale,
	getBrowserTimezone,
	getResolverContext,
	getViewContext,
	selectOptimalInvocationResponse,
} from './utils';
import type { InnerIframeProps, ComponentMap, HTMLCustomIFrameElement } from './types';
import { isAppConsentedLocally, setAppConsentLocal } from './localAppConsent';
import { getLoadingComponent } from './utils/getLoadingComponent';
import { SANDBOX_DEPRECATION_ERROR } from '../../renderer/ForgeDeprecationMessage';
import { useResizeAnalytics } from '../../performance-profiling/useResizeAnalytics';
import { isCustomUI } from '../../utils';
import { egressPermissionService } from '../../utils/useEgressPermissionService';
import { getFeatureFlags } from '../../web-client/queries/getFeatureFlags';
import { useRemoteFetchClient } from '../bridge/methods/useRemoteFetchClient';
import {
	PERMISSION_METHOD_PREFIX,
	permissionMethods,
} from '../bridge/methods/permissions/usePermission';
import { getAppPerfTrackingEnabled } from '../../performance-profiling';

type InvokeRetry = () => Promise<void>;

const styles = cssMap({
	threeLOInfo: {
		width: '100%',
		height: '100%',
	},
	box: {
		position: 'relative',
	},
});

// TODO: no styles defined for resizable modals yet.
// Only Jira enables resizing on modals at the moment.
const modalIframeStyles = css({
	borderRadius: token('radius.small', '3px'),
	height: '100%',
});

const iframeStyles = css({
	border: 'none',
	display: 'block',
	width: '100%',
});

const hiddenIframeStyles = css({
	display: 'none',
});

const nonResizeableIframeStyles = css({
	height: '100%',
});

const defaultComponents: ComponentMap = {
	ThreeLOPrompt: (props) => <ThreeLOPrompt {...props} />,
};

/**
 * Hook for determining default IFrame React ref, using parent ref when provided
 *
 * @param parentIframeRef parent IFrame React ref
 * @returns default IFrame React ref
 */
function useDefaultIframeRef(parentIframeRef: React.Ref<HTMLCustomIFrameElement>) {
	const localIframeRef = useRef<HTMLCustomIFrameElement>(null);
	const mergedRef = useMergeRefs([parentIframeRef, localIframeRef]);
	return mergedRef;
}

export const InnerIframe: React.ForwardRefExoticComponent<
	InnerIframeProps & React.RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, InnerIframeProps>(
	(
		{
			accountId,
			extension,
			contextIds,
			apolloClient,
			components,
			coreData,
			extensionData,
			extensionViewData,
			loadingComponent,
			onLoad,
			height = '100%',
			width = '100%',
			src,
			bridge,
			customBridgeMethods,
			isResizable,
			getContextToken,
			timezone,
			locale,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			extensionPayload,
			isHidden,
			theme,
			surfaceColor,
			appId,
			isInModal,
			setIsLoading,
			onError,
			entryPoint,
			isEmbeddedMacro,
			embeddedParentLocalId,
		},
		parentIframeRef,
	) => {
		const { ThreeLOPrompt } = components ? components(defaultComponents) : defaultComponents;

		const appIsAuthenticated = isAppConsentedLocally(appId, accountId);
		const {
			appVersion,
			consentUrl,
			currentUserConsent,
			requiresUserConsent,
			properties: { frameId },
		} = extension;
		const [userConsentRequired, setUserConsentRequired] = useState(
			consentUrl && !currentUserConsent && requiresUserConsent && !appIsAuthenticated,
		);

		const productContext = useMemo(() => {
			const resolverContext = getResolverContext({ ...coreData, appVersion }, extensionData);
			const viewContext = getViewContext({
				resolverContext,
				accountId,
				extension,
				timezone: timezone ?? getBrowserTimezone(),
				locale: locale ?? getBrowserLocale(),
				extensionPayload,
				extensionViewData,
				theme,
				surfaceColor,
				location: window.location.toString(),
			});
			return { resolverContext, viewContext };
		}, [
			appVersion,
			coreData,
			extensionData,
			extensionViewData,
			accountId,
			extension,
			timezone,
			locale,
			extensionPayload,
			theme,
			surfaceColor,
		]);

		// For embedded macros, create context token options to pass to getContextToken
		// This ensures the token is generated for the embedded macro's context, not the parent's
	const embeddedMacroContextTokenOptions = isEmbeddedMacro
		? {
				extensionOverride: {
					extension,
					localId: coreData.localId,
				},
			}
		: undefined;

		/**
		 * Wrapper for getContextToken that handles both embedded macro and regular cases.
		 * For embedded macros, includes the extension override to generate a token scoped to the embedded macro.
		 * Falls back to cached token when getContextToken is not available.
		 */
		const getContextTokenWrapper = async (contextIdsOverride?: string[]): Promise<string | undefined> => {
			if (!getContextToken) {
				return contextTokenRef.current?.jwt;
			}

			if (isEmbeddedMacro) {
				return getContextToken({
					extensionOverride: {
						extension,
						localId: coreData.localId,
					},
					...(contextIdsOverride && { contextIdsOverride }),
				});
			}

			return contextIdsOverride
				? getContextToken({ contextIdsOverride })
				: getContextToken();
		};

		const tracing = useTracingContext();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const contextTokenRef = useRef<ForgeContextToken | undefined>(undefined);

		const [threeLOInfo, setThreeLOInfo] = useState<{
			authUrl: string;
			onReject: () => void;
			onSuccess: () => Promise<void>;
		} | null>(null);

		const invokeRetriesRef = useRef<Array<InvokeRetry>>([]);

		const iframeRef = useDefaultIframeRef(parentIframeRef);

		useEffect(() => {
			const clearThreeLOInfo = async () => setThreeLOInfo(null);

			if (userConsentRequired) {
				tracing?.recordConsentFlow('consent flow - start');

				setThreeLOInfo({
					authUrl: consentUrl!,
					onSuccess: async () => {
						tracing?.recordConsentFlow('consent flow - end', 'success');
						setUserConsentRequired(false);
						setIsThreeLOPromptVisible(false);
						setAppConsentLocal(appId, accountId);
						return clearThreeLOInfo();
					},
					onReject: () => {
						tracing?.recordConsentFlow('consent flow - end', 'rejected');
						return clearThreeLOInfo();
					},
				});
				setIsThreeLOPromptVisible(true);
				setShouldShowRenderer?.(false);

				if (onLoad) {
					onLoad();
				}
			}
		}, [
			onLoad,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			tracing,
			appId,
			accountId,
			consentUrl,
			userConsentRequired,
		]);

		const navigation = useNavigation({
			extension,
			push: bridge?.spaPush,
		});

		const invoker = useInvokeExtension(contextIds, extension.id, {
			client: apolloClient,
		});

		// The hash generated here should match the one created in the Realtime service when it hashes extension.context
		// in the FCT.
		// If this is flaky because of differences between extensionData and the FCT's extension.context, then
		// use the decoded FCT context directly here as well.
		const extensionContext = (() => {
			try {
				const {
					// eslint-disable-next-line no-unused-vars
					modal,
					...productExtensionData
				} = extensionData;
				return hash(productExtensionData || {});
				// eslint-disable-next-line no-unused-vars
			} catch (_) {
				// eslint-disable-next-line no-console
				console.error('Product context is not a serializable object.');
				return '';
			}
		})();

		// the realtime hooks below have no side effect (i.e. does not actively trigger any network calls or actively makes any subscription).
		// It is because everything inside this hook is lazily initialized.
		// Importantly:
		// - SubscribeRealtimeChannel is called by the client (i.e. @forge/bridge)
		const subscribeRealtimeChannel = useSubscribeRealtimeChannel(
			extension.installationId,
			extensionContext,
			getContextToken,
			fg('platform_embedded_macro_fct_override')
				? embeddedMacroContextTokenOptions
				: undefined,
		);

		const publishRealtimeChannel = usePublishRealtimeChannel(
			extension.installationId,
			extensionContext,
			{
				client: apolloClient,
			},
		);

		const renderThreeLOPrompt = (retry: () => Promise<void>) => {
			return new Promise<void>((resolve, reject) => {
				const authUrl = threeLOInfo?.authUrl || consentUrl;
				if (authUrl) {
					invokeRetriesRef.current.push(retry);

					tracing?.recordConsentFlow('consent flow - start');

					setThreeLOInfo({
						authUrl,
						onSuccess: () => {
							tracing?.recordConsentFlow('consent flow - end', 'success');
							resolve();
							setThreeLOInfo(null);
							setIsThreeLOPromptVisible(false);
							setShouldShowRenderer?.(true);
							return Promise.all(invokeRetriesRef.current.map((retry) => retry())).then(() => {
								invokeRetriesRef.current = [];
							});
						},
						onReject: () => {
							tracing?.recordConsentFlow('consent flow - end', 'rejected');
							return reject();
						},
					});
					setIsThreeLOPromptVisible(true);
					setShouldShowRenderer?.(false);
				}
			});
		};

		const environment = useContext(EnvironmentContext);
		const { cloudId, workspaceId } = coreData;
		const rovoChat = useRovoAgentsChat(
			extension.type,
			appId,
			cloudId,
			apolloClient,
			extension.installationId,
			extension.environmentId,
		);
		const productFetchClient = useProductFetchClient({
			appId,
			environment,
			contextIds,
			cloudId,
			workspaceId,
			extensionId: extension.id,
			options: { client: apolloClient },
			renderThreeLOPrompt,
		});

		const permissions = usePermission({
			appId,
			installationId: extension.installationId,
			contextIds,
			extensionId: extension.id,
			options: { client: apolloClient },
			renderThreeLOPrompt,
		});

		const emitPublicEvent = useEmitPublicEvent(
			contextIds[0],
			{
				appId: extension.appId,
				id: extension.id.split('/').slice(0, 3).join('/'),
				installationId: extension.installationId,
			},
			apolloClient,
		);

		const fetchRemoteClient = useRemoteFetchClient({ client: apolloClient });

		const permissionFunctions = Object.fromEntries(
			permissionMethods.map((method) => [
				`${PERMISSION_METHOD_PREFIX}${method}`,
				permissions[method],
			]),
		);

		const {
			loading,
			iframeProps: { onLoad: onLoadIframeFromProps },
		} = useBridge({
			cloudId,
			theme,
			surfaceColor,
			origin: new URL(src).origin,
			api: {
				...createBridgeApi(bridge, {
					userConsentRequired: !!userConsentRequired,
				}),
				...(fg('platform_forge_ui_open_rovo_bridge') ? { openRovo: rovoChat.openRovo } : {}),
				onInvoke: async (payload) => {
					const newContextToken = fg('platform_embedded_macro_fct_override')
						? await getContextTokenWrapper(contextIds)
						: getContextToken
							? await getContextToken({ contextIdsOverride: contextIds })
							: contextTokenRef.current?.jwt;

					tracing?.recordGqlCall('invokeExtension - start');

					const { data } = await invoker({
						call: payload,
						context: {
							...productContext.resolverContext,
						},
						extensionPayload,
						contextToken: newContextToken,
					});

					// Default to using the regular invocation response
					var resp = data?.invokeExtension;

					// If the comparison feature flag is enabled, perform a shadow invocation to compare responses
					if (fg('forge_ui_use_get_context_token_compare')) {
						// Get shadow context token from cached token (when getContextToken is defined, it's already used above)
						const shadowContextToken = contextTokenRef.current?.jwt;

						// Perform shadow invocation with the shadow token
						const { data: shadowData } = await invoker({
							call: payload,
							context: {
								...productContext.resolverContext,
							},
							extensionPayload,
							contextToken: shadowContextToken,
						});

						// Compare responses and select the optimal one based on token context differences
						const selectedResponse = selectOptimalInvocationResponse(
							data?.invokeExtension,
							shadowData?.invokeExtension,
							createAnalyticsEvent,
						);

						if (selectedResponse) {
							resp = selectedResponse;
						}
					}

					if (!resp?.success) {
						const error = resp?.errors?.[0];
						if (error?.extensions.errorType === 'USER_CONSENT_REQUIRED') {
							return {
								type: '3lo',
								authUrl: error?.extensions.fields?.authInfoUrl as string,
							};
						}
						if (
							error?.extensions.errorType === SANDBOX_DEPRECATION_ERROR &&
							onError &&
							fg('platform_render_sandbox_runtime_error_panel')
						) {
							onError(error?.extensions.errorType);
						}
						return {
							type: 'err',
							message: error?.message ?? 'An error occurred while trying to invoke.',
						};
					}

					tracing?.recordGqlCall('invokeExtension - end');

					contextTokenRef.current = resp?.contextToken;
					return {
						type: 'ok',
						response: resp?.response?.body,
					};
				},
				onInitFeatureFlags: async (data) => {
					const newContextToken = fg('platform_embedded_macro_fct_override')
						? await getContextTokenWrapper()
						: getContextToken
							? await getContextToken()
							: contextTokenRef.current?.jwt;

					tracing?.recordBridgeCall('getFeatureFlags - start');

					try {
						const resp = await getFeatureFlags(appId, data.user, environment, newContextToken);
						tracing?.recordBridgeCall('getFeatureFlags - end');

						return {
							type: 'ok',
							response: resp,
						};
					} catch (err) {
						tracing?.recordBridgeCall('getFeatureFlags - end');
						// eslint-disable-next-line no-console
						console.error('There were errors while initialising feature flags client ', err);
						return {
							type: 'err',
							message: 'An error occurred while trying to initialise feature flags client.',
						};
					}
				},

				onNavigate: navigation.onNavigate,
				onThreeLO: async (authUrl, retry: InvokeRetry) => {
					return new Promise((resolve, reject) => {
						invokeRetriesRef.current.push(retry);

						tracing?.recordConsentFlow('consent flow - start');

						setThreeLOInfo({
							authUrl,
							onSuccess: () => {
								tracing?.recordConsentFlow('consent flow - end', 'success');
								resolve();
								setThreeLOInfo(null);
								setIsThreeLOPromptVisible(false);
								setShouldShowRenderer?.(true);

								return Promise.all(invokeRetriesRef.current.map((retry) => retry())).then(() => {
									invokeRetriesRef.current = [];
								});
							},
							onReject: () => {
								tracing?.recordConsentFlow('consent flow - end', 'rejected');
								return reject();
							},
						});
						setIsThreeLOPromptVisible(true);
						setShouldShowRenderer?.(false);
					});
				},
				getContext: async () => ({
					...productContext.viewContext,
				}),
				fetchProduct: async ({ restPath, product, fetchRequestInit, isMultipartFormData }) => {
					const response = await productFetchClient(
						restPath,
						product,
						fetchRequestInit,
						isMultipartFormData,
					);

					const { status, statusText } = response;
					const headers = Object.fromEntries((response.headers as any).entries());
					const isAttachment = isBinaryContentType(response.headers.get('content-type'));
					const body = response.body ? await response.text() : undefined;

					return { headers, status, statusText, body, isAttachment };
				},
				fetchRemote: async ({ remoteKey, fetchRequestInit, isMultipartFormData }) => {
					const { path, ...init } = fetchRequestInit;
					const contextToken = fg('platform_embedded_macro_fct_override')
						? await getContextTokenWrapper()
						: getContextToken
							? await getContextToken()
							: contextTokenRef.current?.jwt;
					const response = await fetchRemoteClient(
						remoteKey,
						init,
						isMultipartFormData,
						path,
						contextToken,
					);

					const { status, statusText } = response;
					const headers = Object.fromEntries((response.headers as any).entries());
					const isAttachment = isBinaryContentType(response.headers.get('content-type'));
					const body = response.body ? await response.text() : undefined;

					return { headers, status, statusText, body, isAttachment };
				},
				getFrameId: () => frameId ?? null,
				subscribeRealtimeChannel: async (payload: SubscribeRealtimeChannelPayload) => {
					return subscribeRealtimeChannel(payload);
				},
				publishRealtimeChannel: async (payload: PublishRealtimeChannelPayload) => {
					const contextToken = fg('platform_embedded_macro_fct_override')
						? await getContextTokenWrapper()
						: getContextToken
							? await getContextToken()
							: contextTokenRef.current?.jwt;
					return publishRealtimeChannel(payload, contextToken);
				},
				...(fg('forge-ui-add-public-event-methods') ? { emitPublic: emitPublicEvent } : {}),
				...permissionFunctions,
			},
			customBridgeMethods,
			onLoad,
			isResizable,
			height,
			environment,
			extension,
			iframeRef,
			client: apolloClient,
			contextIds,
			isInModal,
			localId: coreData.localId,
			isEmbeddedMacro,
			embeddedParentLocalId,
			extensionPayload,
			extensionData,
		});

		useEffect(() => {
			setIsLoading?.(loading);
		}, [loading, setIsLoading]);

		const { containerRef } = useResizeAnalytics({
			// need to check if the extension is custom UI as well since this component is used in WebRuntime for UI Kit
			isEnabled: isCustomUI(extension, entryPoint) && getAppPerfTrackingEnabled(),
		});

		const sandboxAttributeRules = [
			'allow-downloads',
			'allow-forms',
			'allow-modals',
			'allow-pointer-lock',
			'allow-same-origin',
			'allow-scripts',
		];

		try {
			const egressService = egressPermissionService('FETCH_CLIENT_SIDE', extension.egress);
			const containsWildCardEgress = egressService.containsWildCardEgress();

			if (containsWildCardEgress) {
				sandboxAttributeRules.push('allow-popups', 'allow-popups-to-escape-sandbox');
			}
			// eslint-disable-next-line no-unused-vars
		} catch (_) {
			// swallow error
		}

		let framePermissionsPolicy = 'camera; clipboard-write; display-capture; fullscreen; microphone';
		if (fg('platform_forge-ui_frame-allow-local-network-access')) {
			framePermissionsPolicy += '; local-network-access';
		}

		return (
			<Box
				ref={containerRef}
				xcss={styles.box}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					height: isResizable || threeLOInfo ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					width,
				}}
			>
				{navigation.getModalJsx()}
				{permissions.getModalJsx()}
				{(loading || threeLOInfo) && (
					<Box xcss={styles.threeLOInfo}>
						{loading && !threeLOInfo && !isHidden && getLoadingComponent(loadingComponent)}
						{threeLOInfo &&
							ThreeLOPrompt({
								authUrl: threeLOInfo.authUrl,
								onSuccess: threeLOInfo.onSuccess,
								metadata: {
									moduleType: extension.type,
								},
							})}
					</Box>
				)}
				{!userConsentRequired && (
					<Fragment>
						<iframe
							data-testid="hosted-resources-iframe"
							data-forge-iframe
							css={[
								iframeStyles,
								(loading || threeLOInfo || isHidden) && hiddenIframeStyles,
								isInModal && modalIframeStyles,
								!isResizable && nonResizeableIframeStyles,
							]}
							sandbox={sandboxAttributeRules.join(' ')}
							allow={framePermissionsPolicy}
							src={src}
							ref={iframeRef}
							onLoad={onLoadIframeFromProps}
							// eslint-disable-next-line @atlaskit/platform/prefer-crypto-random-uuid -- Use crypto.randomUUID instead
							title={`Iframe ${frameId ?? uuid()} from ${appId}`}
						/>
					</Fragment>
				)}
			</Box>
		);
	},
);
